import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api } from "@/lib/api";
import { setKnownAuthzVersion } from "@/lib/authzVersion";

/*
 * ============================================================================
 * AUTENTICAÇÃO + AUTORIZAÇÃO WEB
 * ============================================================================
 *
 * Fonte de verdade:
 *   backend / authorization/engine.py
 *
 * O frontend consome somente a autorização efetiva retornada pelo backend.
 * Não replica a matriz, o escopo ou as regras de governança.
 *
 * Este contexto:
 *   - mantém a sessão do usuário;
 *   - carrega /auth/me;
 *   - carrega /permissions/me;
 *   - expõe a autorização efetiva ao frontend;
 *   - NÃO cria uma matriz paralela de permissões.
 *
 * A interface usa este contexto apenas para navegação/UX.
 * A segurança definitiva continua no backend.
 */

const AuthContext = createContext(null);

function objectOrEmpty(value) {
  return value &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? value
    : {};
}

function normalizeBooleanMap(value) {
  const source = objectOrEmpty(value);

  return Object.fromEntries(
    Object.entries(source).map(
      ([key, enabled]) => [
        key,
        Boolean(
          enabled === true ||
            enabled === 1 ||
            enabled === "1" ||
            enabled === "true",
        ),
      ],
    ),
  );
}

function normalizeAuthorization(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const permissions =
    normalizeNestedBooleanMap(data.permissions);

  const modules =
    objectOrEmpty(data.modules);

  const modulePermissions =
    normalizeNestedBooleanMap(
      data.module_permissions,
    );

  const permissionMatrix =
    objectOrEmpty(
      data.permission_matrix,
    );

  const features =
    normalizeBooleanMap(
      data.features,
    );

  const scope =
    normalizeScope(data.scope);

  const scopeMatrix =
    objectOrEmpty(
      data.scope_matrix,
    );

  const configAccess =
    normalizeBooleanMap(
      data.config_access ??
        data.config_groups,
    );

  const delegation =
    objectOrEmpty(data.delegation);

  const rolePolicy =
    objectOrEmpty(data.role_policy);

  const metadata =
    objectOrEmpty(
      data.metadata,
    );

  const authorizationVersion =
    data.authorization_context_version ??
    data.authorization_version ??
    data.matrix_version ??
    metadata.matrix_version ??
    data.version ??
    null;

  const authorizationGeneratedAt =
    data.authorization_generated_at ??
    data.generated_at ??
    metadata.generated_at ??
    null;

  /*
   * `modules` vem exclusivamente do motor central.
   * Não reconstruímos módulos a partir das permissões no frontend.
   */
  const effectiveModules = modules;

  return {
    ...data,

    permissions,

    modules:
      effectiveModules,

    module_permissions:
      modulePermissions,

    permission_matrix:
      Object.keys(
        permissionMatrix,
      ).length
        ? permissionMatrix
        : permissions,

    features,

    scope,

    scope_matrix:
      scopeMatrix,

    scope_levels: [
      "company_ids",
      "client_ids",
      "unit_ids",
      "area_ids",
      "subarea_ids",
    ],

    scope_summary: {
      global: Boolean(
        data.global ??
          scope.global ??
          scopeMatrix.global,
      ),
      company_count:
        scope.company_ids.length,
      client_count:
        scope.client_ids.length,
      unit_count:
        scope.unit_ids.length,
      area_count:
        scope.area_ids.length,
      subarea_count:
        scope.subarea_ids.length,
    },

    config_access:
      configAccess,

    config_groups:
      configAccess,

    delegation,

    role_policy:
      rolePolicy,

    // ================================================================
    // TRÊS CONCEITOS FUNDAMENTAIS (separação explícita)
    // ================================================================

    // PRIMEIRO CONCEITO: O que o cliente possui (ClientModuleEntitlement)
    clientModules:
      data.client_modules ||
      {},

    // SEGUNDO CONCEITO: Quais dados o usuário pode enxergar (UserClientMembership)
    userClients:
      data.user_clients ||
      {},

    // TERCEIRO CONCEITO: O que o usuário pode fazer (UserPermissionGrant)
    userPermissions:
      data.user_permissions ||
      {},

    metadata,

    authorization_context_version:
      authorizationVersion,

    authorization_generated_at:
      authorizationGeneratedAt,

    global: Boolean(
      data.global ??
        scope.global ??
        scopeMatrix.global,
    ),
  };
}

function normalizeScopeLevel(value) {
  const aliases = {
    empresa: "company_ids",
    empresas: "company_ids",
    company: "company_ids",
    cliente: "client_ids",
    clientes: "client_ids",
    client: "client_ids",
    unidade: "unit_ids",
    unidades: "unit_ids",
    unit: "unit_ids",
    area: "area_ids",
    áreas: "area_ids",
    areas: "area_ids",
    subarea: "subarea_ids",
    subárea: "subarea_ids",
    subareas: "subarea_ids",
    subáreas: "subarea_ids",
  };

  return aliases[String(value || "").trim().toLowerCase()] || value;
}

function normalizeScope(scope) {
  const source = objectOrEmpty(scope);
  const normalized = {};

  for (const key of [
    "company_ids",
    "client_ids",
    "unit_ids",
    "area_ids",
    "subarea_ids",
  ]) {
    const values = Array.isArray(source[key])
      ? source[key]
      : source[key] === undefined ||
        source[key] === null ||
        source[key] === ""
      ? []
      : [source[key]];

    normalized[key] = [
      ...new Set(
        values
          .map((value) => String(value).trim())
          .filter(Boolean),
      ),
    ];
  }

  if (typeof source.global === "boolean") {
    normalized.global = source.global;
  }

  return {
    ...source,
    ...normalized,
  };
}

function normalizeNestedBooleanMap(value) {
  const source = objectOrEmpty(value);

  return Object.fromEntries(
    Object.entries(source).map(([module, actions]) => [
      module,
      normalizeBooleanMap(actions),
    ]),
  );
}

function normalizeRole(role) {
  const value = String(
    role || "",
  )
    .trim()
    .toLowerCase();

  if (value === "rt") {
    return "responsavel_tecnico";
  }

  return value;
}

export function AuthProvider({
  children,
}) {
  const [user, setUser] =
    useState(null);

  const [
    authorization,
    setAuthorization,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    authorizationLoading,
    setAuthorizationLoading,
  ] = useState(false);

  const [
    authorizationError,
    setAuthorizationError,
  ] = useState(null);

  const [
    runtimeConfig,
    setRuntimeConfig,
  ] = useState(null);

  /*
   * =========================================================================
   * SESSÃO
   * =========================================================================
   */

  const clearSession =
    useCallback(() => {
      setUser(null);
      setAuthorization(null);
      setKnownAuthzVersion(null);
      setAuthorizationError(null);
      setAuthorizationLoading(false);
      setRuntimeConfig(null);
    }, []);

  /*
   * =========================================================================
   * RUNTIME CONFIG
   * =========================================================================
   */

  const loadRuntimeConfig =
    useCallback(async () => {
      try {
        const { data } =
          await api.get(
            "/platform/runtime-config",
          );

        setRuntimeConfig(
          data || null,
        );

        return data || null;
      } catch (error) {
        console.warn(
          "Configuração de runtime não disponível:",
          error,
        );

        return null;
      }
    }, []);

  /*
   * =========================================================================
   * AUTORIZAÇÃO
   * =========================================================================
   */

  const loadAuthorization =
    useCallback(async () => {
      setAuthorizationLoading(
        true,
      );
      setAuthorizationError(
        null,
      );

      try {
        const { data } =
          await api.get(
            "/permissions/me",
          );

        const effective =
          normalizeAuthorization(
            data,
          );

        if (
          !effective ||
          typeof effective !== "object" ||
          !effective.modules ||
          !effective.permissions
        ) {
          throw new Error(
            "O backend não retornou uma autorização efetiva completa.",
          );
        }

        setAuthorization(
          effective,
        );
        // Base de comparação do cabeçalho X-Astral-Authz-Version.
        setKnownAuthzVersion(
          data?.authz_version,
        );

        return effective;
      } catch (error) {
        setAuthorizationError(
          error,
        );

        throw error;
      } finally {
        setAuthorizationLoading(
          false,
        );
      }
    }, []);

  /*
   * =========================================================================
   * SESSÃO INICIAL
   * =========================================================================
   */

  const loadSession =
    useCallback(async () => {
      setLoading(true);

      try {
        const { data: authUser } =
          await api.get(
            "/auth/me",
          );

        if (!authUser) {
          clearSession();
          return null;
        }

        setUser(authUser);

        await loadAuthorization();
        void loadRuntimeConfig();

        return authUser;
      } catch (error) {
        console.error(
          "Falha ao carregar sessão/autorização:",
          error,
        );

        clearSession();
        return null;
      } finally {
        setLoading(false);
      }
    }, [
      clearSession,
      loadAuthorization,
      loadRuntimeConfig,
    ]);

  /*
   * =========================================================================
   * BOOTSTRAP
   * =========================================================================
   */

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        await loadSession();
      } catch (error) {
        if (!mounted) {
          return;
        }

        console.error(
          "Falha ao inicializar sessão:",
          error,
        );

        clearSession();
      }
    };

    void initialize();

    return () => {
      mounted = false;
    };
  }, [
    clearSession,
    loadSession,
  ]);


  /*
   * =========================================================================
   * LOGIN
   * =========================================================================
   */

  const login =
    useCallback(
      async (
        email,
        password,
        rememberMe = false,
      ) => {
        const { data } =
          await api.post(
            "/auth/login",
            {
              email,
              password,
              remember_me:
                Boolean(
                  rememberMe,
                ),
            },
          );

        setUser(data);

        try {
          await loadAuthorization();
          void loadRuntimeConfig();
        } catch (error) {
          /*
           * O login só fica efetivo para a UI se conseguirmos sincronizar
           * a autorização. Isso evita renderizar uma sessão sem política.
           */
          clearSession();

          console.error(
            "Falha ao carregar autorização após login:",
            error,
          );

          throw error;
        }

        return data;
      },
      [
        clearSession,
        loadAuthorization,
        loadRuntimeConfig,
      ],
    );

  /*
   * =========================================================================
   * REFRESH DA AUTORIZAÇÃO
   * =========================================================================
   */

  const refreshAuthorization =
    useCallback(
      async () => {
        try {
          const effective =
            await loadAuthorization();

          try {
            const {
              data: freshUser,
            } = await api.get(
              "/auth/me",
            );

            if (freshUser) {
              setUser(
                freshUser,
              );
            }
          } catch (error) {
            console.warn(
              "Autorização atualizada, mas não foi possível atualizar /auth/me:",
              error,
            );
          }

          return effective;
        } catch (error) {
          console.error(
            "Falha ao atualizar autorização:",
            error,
          );

          throw error;
        }
      },
      [loadAuthorization],
    );

  /*
   * =========================================================================
   * REFRESH DE ESCOPO
   * =========================================================================
   */

  const refreshScope =
    useCallback(
      async () => {
        const effective =
          await loadAuthorization();

        return effective?.scope || {};
      },
      [loadAuthorization],
    );

  /*
   * =========================================================================
   * SINCRONIZAÇÃO CONTÍNUA DA AUTORIZAÇÃO
   * =========================================================================
   *
   * Alterações feitas pelo Platform Admin precisam chegar aos consumidores
   * sem depender de logout/login. O backend continua sendo a única fonte
   * da autorização efetiva.
   */
  useEffect(() => {
    if (!user) {
      return undefined;
    }

    let cancelled = false;
    let running = false;

    const sync = async () => {
      if (
        cancelled ||
        running ||
        authorizationLoading
      ) {
        return;
      }

      running = true;

      try {
        await refreshAuthorization();
        await loadRuntimeConfig();
      } catch (error) {
        if (!cancelled) {
          console.warn(
            "Falha ao sincronizar autorização efetiva:",
            error,
          );
        }
      } finally {
        running = false;
      }
    };

    const onAuthorizationChanged = () => {
      void sync();
    };

    const onFocus = () => {
      void sync();
    };

    const onVisibility = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        void sync();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener(
        "astral:authorization-changed",
        onAuthorizationChanged,
      );
      window.addEventListener(
        "astral:modules-changed",
        onAuthorizationChanged,
      );
      window.addEventListener(
        "focus",
        onFocus,
      );
    }

    if (typeof document !== "undefined") {
      document.addEventListener(
        "visibilitychange",
        onVisibility,
      );
    }

    return () => {
      cancelled = true;

      if (typeof window !== "undefined") {
        window.removeEventListener(
          "astral:authorization-changed",
          onAuthorizationChanged,
        );
        window.removeEventListener(
          "astral:modules-changed",
          onAuthorizationChanged,
        );
        window.removeEventListener(
          "focus",
          onFocus,
        );

      }

      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          onVisibility,
        );
      }
    };
  }, [
    user,
    authorizationLoading,
    refreshAuthorization,
    loadRuntimeConfig,
  ]);

  /*
   * =========================================================================
   * LOGOUT
   * =========================================================================
   */

  const logout =
    useCallback(
      async () => {
        try {
          await api.post(
            "/auth/logout",
          );
        } catch (error) {
          console.warn(
            "Falha no logout do backend; sessão local será encerrada:",
            error,
          );
        } finally {
          clearSession();
        }
      },
      [clearSession],
    );

  /*
   * =========================================================================
   * USUÁRIO EFETIVO
   * =========================================================================
   */

  const effectiveUser =
    useMemo(() => {
      if (!user) {
        return user;
      }

      if (!authorization) {
        return user;
      }

      return {
        ...user,
        ...authorization,

        id:
          user.id ||
          authorization.user_id,

        user_id:
          authorization.user_id ||
          user.id,

        role:
          authorization.role ||
          user.role,

        permissions:
          authorization.permissions,

        modules:
          authorization.modules,

        module_permissions:
          authorization.module_permissions,

        permission_matrix:
          authorization.permission_matrix,

        features:
          authorization.features,

        scope:
          authorization.scope,

        scope_matrix:
          authorization.scope_matrix,

        config_access:
          authorization.config_access,

        config_groups:
          authorization.config_groups,

        delegation:
          authorization.delegation,

        role_policy:
          authorization.role_policy,

        runtimeConfig,
      };
    }, [
      user,
      authorization,
      runtimeConfig,
    ]);

  /*
   * =========================================================================
   * HELPERS DE AUTORIZAÇÃO
   * =========================================================================
   *
   * Estes helpers NÃO decidem uma política nova.
   * Eles apenas consultam o resultado entregue pelo backend.
   */

  const currentRole =
    normalizeRole(
      authorization?.role ??
        effectiveUser?.role ??
        user?.role,
    );

  const isAdmin =
    currentRole === "admin";

  const hasModule =
    useCallback(
      (module) => {
        if (!module) {
          return false;
        }

        return Boolean(
          authorization?.modules?.[
            module
          ],
        );
      },
      [
        authorization?.modules,
        isAdmin,
      ],
    );

  const can =
    useCallback(
      (module, action) => {
        if (!module || !action) {
          return false;
        }

        if (!hasModule(module)) {
          return false;
        }

        return Boolean(
          authorization?.permissions?.[
            module
          ]?.[action],
        );
      },
      [
        authorization?.permissions,
        hasModule,
        isAdmin,
      ],
    );

  const hasFeature =
    useCallback(
      (feature) => {
        if (!feature) {
          return false;
        }

        return Boolean(
          authorization?.features?.[
            feature
          ],
        );
      },
      [
        authorization?.features,
        isAdmin,
      ],
    );

  const hasConfig =
    useCallback(
      (group) => {
        if (!group) {
          return false;
        }

        return Boolean(
          authorization
            ?.config_access?.[
              group
            ],
        );
      },
      [
        authorization?.config_access,
        isAdmin,
      ],
    );

  const hasScope = useCallback(
    (level, id = null) => {
      const key = normalizeScopeLevel(level);

      if (![
        "company_ids",
        "client_ids",
        "unit_ids",
        "area_ids",
        "subarea_ids",
      ].includes(key)) {
        return false;
      }

      if (
        authorization?.scope?.global === true
      ) {
        return true;
      }

      const ids = Array.isArray(
        authorization?.scope?.[key],
      )
        ? authorization.scope[key]
        : [];

      if (id === null || id === undefined) {
        return ids.length > 0;
      }

      return ids.includes(
        String(id).trim(),
      );
    },
    [
      authorization?.scope,
      isAdmin,
    ],
  );

  const authorizationDiagnostic =
    useCallback(
      () => ({
        userId:
          effectiveUser?.id ||
          user?.id ||
          null,
        role:
          currentRole ||
          null,
        modules:
          authorization?.modules || {},
        permissions:
          authorization?.permissions || {},
        scope:
          authorization?.scope || {},
        scopeSummary:
          authorization?.scope_summary || {},
        rolePolicy:
          authorization?.role_policy || {},
        version:
          authorization?.authorization_context_version ??
          authorization?.authorization_version ??
          authorization?.matrix_version ??
          null,
        generatedAt:
          authorization?.authorization_generated_at ??
          authorization?.generated_at ??
          null,
      }),
      [
        effectiveUser?.id,
        user?.id,
        currentRole,
        authorization,
      ],
    );

  const notifyAuthorizationChanged =
    useCallback(
      () => {
        if (
          typeof window !== "undefined"
        ) {
          window.dispatchEvent(
            new Event(
              "astral:authorization-changed",
            ),
          );
        }
      },
      [],
    );

  const value =
    useMemo(
      () => ({
        /*
         * Identidade.
         */
        user:
          effectiveUser,
        identity:
          user,

        /*
         * Estado.
         */
        loading,
        authorizationLoading,
        authorizationError,

        /*
         * Autorização central.
         */
        authorization,
        runtimeConfig,

        /*
         * Contratos diretos.
         */
        permissions:
          authorization?.permissions ||
          {},
        modules:
          authorization?.modules ||
          {},
        modulePermissions:
          authorization?.module_permissions ||
          {},
        permissionMatrix:
          authorization?.permission_matrix ||
          {},
        permission_matrix:
          authorization?.permission_matrix ||
          {},
        features:
          authorization?.features ||
          {},
        scope:
          authorization?.scope ||
          {},
        scopeMatrix:
          authorization?.scope_matrix ||
          {},
        configAccess:
          authorization?.config_access ||
          {},
        config_groups:
          authorization?.config_groups ||
          {},
        delegation:
          authorization?.delegation ||
          {},
        rolePolicy:
          authorization?.role_policy ||
          {},

        /*
         * TRÊS CONCEITOS FUNDAMENTAIS (separação explícita).
         *
         * 1. clientModules → O que o cliente possui
         * 2. userClients   → Quais dados o usuário pode enxergar
         * 3. userPermissions → O que o usuário pode fazer
         */
        clientModules:
          authorization?.clientModules ||
          authorization?.client_modules ||
          {},

        userClients:
          authorization?.userClients ||
          authorization?.user_clients ||
          {},

        userPermissions:
          authorization?.userPermissions ||
          authorization?.user_permissions ||
          {},

        authorizationVersion:
          authorization?.authorization_context_version ??
          authorization?.authorization_version ??
          authorization?.matrix_version ??
          null,

        authorizationGeneratedAt:
          authorization?.authorization_generated_at ??
          authorization?.generated_at ??
          null,

        authorizationMetadata:
          authorization?.metadata ||
          {},

        role:
          currentRole,
        isAdmin,
        isGlobal:
          Boolean(
            isAdmin ||
              authorization?.global,
          ),

        /*
         * Helpers.
         */
        hasModule,
        can,
        hasFeature,
        hasScope,
        hasConfig,
        authorizationDiagnostic,

        /*
         * Operações.
         */
        login,
        logout,
        loadSession,
        refreshAuthorization,
        refreshScope,
        refreshAuthorizationNow:
          refreshAuthorization,
        notifyAuthorizationChanged,
      }),
      [
        effectiveUser,
        user,
        loading,
        authorizationLoading,
        authorizationError,
        authorization,
        runtimeConfig,
        currentRole,
        isAdmin,
        hasModule,
        can,
        hasFeature,
        hasScope,
        hasConfig,
        authorizationDiagnostic,
        login,
        logout,
        loadSession,
        refreshAuthorization,
        refreshScope,
        notifyAuthorizationChanged,
        // Três conceitos
        authorization?.clientModules,
        authorization?.client_modules,
        authorization?.userClients,
        authorization?.user_clients,
        authorization?.userPermissions,
        authorization?.user_permissions,
      ],
    );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth =
  () => {
    const context =
      useContext(
        AuthContext,
      );

    if (!context) {
      throw new Error(
        "useAuth deve ser utilizado dentro de AuthProvider.",
      );
    }

    return context;
  };

export default AuthContext;
